import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserProfile, ChatMessage, Timestamp, Page, PageContextType } from '../types';
import { useChat } from '../ChatContext';
import { useAuth } from '../AuthContext';
import { PageContext } from '../PageContext';
import ImageViewerModal from '../components/ImageViewerModal';
import CameraModal from '../components/CameraModal';
import { uploadFile, base64ToBlob } from '../storageUtils';
import { isUserOnline } from '../utils';
import InteractionMenu from '../components/InteractionMenu';
import GiphyPicker from '../components/GiphyPicker';

interface ChatConversationPageProps {
    recipient: UserProfile;
}

const ChatConversationPage: React.FC<ChatConversationPageProps> = ({ recipient }) => {
    const { userProfile } = useAuth();
    const { messages, sendMessage, editMessage, deleteMessage, reportMessage, getOrCreateConversation, markAsRead, addReactionToMessage } = useChat();
    const pageContext = useContext<PageContextType | undefined>(PageContext);
    
    const [newMessage, setNewMessage] = useState('');
    const [imageToSend, setImageToSend] = useState<{ file: File | Blob; previewUrl: string } | null>(null);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [viewingImage, setViewingImage] = useState<string | null>(null);
    const [isCameraOpen, setCameraOpen] = useState(false);
    const [isAttachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [showGiphy, setShowGiphy] = useState(false);

    // Interaction Menu State
    const [activeMessage, setActiveMessage] = useState<ChatMessage | null>(null);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
    
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [editModalText, setEditModalText] = useState('');
    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [reportReason, setReportReason] = useState('');
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        const fetchConversation = async () => {
            const convoId = await getOrCreateConversation(recipient.uid);
            setConversationId(convoId);
            markAsRead(convoId);
        };
        fetchConversation();
    }, [recipient.uid, getOrCreateConversation, markAsRead]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, conversationId]);

    const handleSendMessage = async (e?: React.FormEvent, giphyUrl?: string) => {
        if (e) e.preventDefault();
        if ((!newMessage.trim() && !imageToSend && !giphyUrl) || !conversationId) return;
        
        setIsUploading(true);
        let uploadedImageUrl: string | null = giphyUrl || null;
        
        try {
            if (imageToSend && !giphyUrl) {
                try {
                    uploadedImageUrl = await uploadFile(imageToSend.file, `chat-images/${conversationId}`);
                } catch (error) {
                    console.error("Image upload failed", error);
                }
            }
            
            await sendMessage(conversationId, { text: newMessage, imageUrl: uploadedImageUrl || undefined });
            
            setNewMessage('');
            setImageToSend(null);
            setShowGiphy(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

        } catch (error) {
            console.error('Message failed to send.', error);
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setImageToSend({ file, previewUrl: URL.createObjectURL(file) });
        }
        setAttachmentMenuOpen(false);
    };

    const handleImageFromCamera = (base64: string) => {
        const blob = base64ToBlob(base64, 'image/jpeg');
        setImageToSend({ file: blob, previewUrl: base64 });
        setCameraOpen(false);
    };

    // --- Interaction Logic ---
    const handlePressStart = (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>, msg: ChatMessage) => {
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        
        longPressTimer.current = setTimeout(() => {
            setActiveMessage(msg);
            setMenuPosition({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height
            });
            longPressTimer.current = null;
            // Provide haptic feedback if available
            if (navigator.vibrate) navigator.vibrate(50);
        }, 500);
    };

    const handlePressEnd = () => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };
    
    // Generic menu opener
    const openMenuForMessage = (e: React.MouseEvent<HTMLElement>, msg: ChatMessage) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const rect = target.getBoundingClientRect();
        setActiveMessage(msg);
        setMenuPosition({
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height
        });
    };
    
    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>, msg: ChatMessage) => {
        openMenuForMessage(e, msg);
    };
    
    const handleReaction = (emoji: string) => {
        if (activeMessage && conversationId) {
            addReactionToMessage(conversationId, activeMessage.id, emoji);
        }
    };

    const handleAction = async (action: 'reply' | 'copy' | 'share' | 'report' | 'delete') => {
        if (!activeMessage || !conversationId) return;

        switch (action) {
            case 'reply':
                if (activeMessage.text) {
                    setNewMessage(`Replying to: "${activeMessage.text.substring(0, 20)}..." `);
                }
                break;
            case 'copy':
                if (activeMessage.text) {
                    navigator.clipboard.writeText(activeMessage.text);
                    alert('Copied to clipboard');
                }
                break;
            case 'share':
                const textToShare = activeMessage.text || (activeMessage.imageUrl ? 'Check out this image from What Was It!' : '');
                if (navigator.share && textToShare) {
                    try {
                        await navigator.share({
                            title: 'Message from What Was It?',
                            text: textToShare,
                        });
                    } catch (err) {
                        console.error('Error sharing:', err);
                    }
                } else if (textToShare) {
                    navigator.clipboard.writeText(textToShare);
                    alert('Message copied to clipboard for sharing!');
                }
                break;
            case 'report':
                setReportReason('');
                setReportModalOpen(true);
                break;
            case 'delete':
                if (window.confirm("Delete this message?")) {
                    deleteMessage(conversationId, activeMessage.id);
                }
                break;
        }
    };

    const handleEditSubmit = async () => {
        if (!activeMessage || !conversationId) return;
        try {
            await editMessage(conversationId, activeMessage.id, editModalText);
        } catch (e) {
            console.error('Failed to edit message', e);
        }
        setEditModalOpen(false);
    };

    const handleReportSubmit = async () => {
        if (!activeMessage || !conversationId) return;
        try {
            await reportMessage(conversationId, activeMessage.id, activeMessage.senderId, reportReason, "User reported from chat");
            alert('Message reported. Admins will review.');
        } catch (e) {
            console.error('Failed to report message', e);
        }
        setReportModalOpen(false);
    };
    
    const conversationMessages = conversationId ? messages[conversationId] || [] : [];
    const recipientDisplayName = recipient.displayName;
    const recipientPhotoURL = recipient.photoURL;

    return (
        <div className="h-full flex flex-col bg-gray-100">
            {/* Header */}
            <header className="flex-shrink-0 flex items-center p-3 pt-[max(0.75rem,env(safe-area-inset-top))] shadow-md bg-[var(--primary-dark-purple)]">
                <button onClick={() => pageContext?.goBack()} className="text-white mr-3">
                    <i className="ph-duotone ph-arrow-left text-2xl"></i>
                </button>
                <div className="relative flex-shrink-0">
                    <img src={recipientPhotoURL} alt={recipientDisplayName} className="w-10 h-10 rounded-full object-cover"/>
                    {isUserOnline(recipient) && (
                        <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                    )}
                </div>
                <h1 className="text-lg font-bold text-white ml-3">{recipientDisplayName}</h1>
            </header>

            {/* Message List - Added custom-scrollbar class */}
            <main className="flex-grow p-4 overflow-y-auto custom-scrollbar">
                <div className="space-y-1">
                    {conversationMessages.map((msg, index) => {
                        const isMe = msg.senderId === userProfile?.uid;
                        const hasReactions = msg.reactions && Object.keys(msg.reactions).length > 0;
                        const isInteracting = activeMessage?.id === msg.id;

                        return (
                             <div key={msg.id || index} className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group relative`}>
                                <div className={`flex items-end gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}>
                                    {!isMe && (
                                        <div className="relative flex-shrink-0 self-start">
                                            <img src={recipientPhotoURL} alt={recipientDisplayName} className="w-6 h-6 rounded-full object-cover"/>
                                            {isUserOnline(recipient) && (
                                                <span className="absolute bottom-0 right-0 block h-2 w-2 rounded-full bg-green-500 border border-white"></span>
                                            )}
                                        </div>
                                    )}
                                    <div className="relative group">
                                        <div 
                                            className={`max-w-xs md:max-w-md p-1 rounded-2xl relative transition-all duration-200 ${isInteracting ? 'scale-105 z-10 shadow-lg' : ''} ${isMe ? 'bg-purple-600 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none shadow-sm'}`}
                                            onTouchStart={(e) => handlePressStart(e, msg)}
                                            onTouchEnd={handlePressEnd}
                                            onMouseDown={(e) => handlePressStart(e, msg)}
                                            onMouseUp={handlePressEnd}
                                            onMouseLeave={handlePressEnd}
                                            onContextMenu={(e) => handleContextMenu(e, msg)}
                                        >
                                            {msg.imageUrl && (
                                                <img 
                                                    src={msg.imageUrl} 
                                                    alt="Sent image" 
                                                    className="rounded-xl cursor-pointer"
                                                    referrerPolicy="no-referrer"
                                                    onClick={() => setViewingImage(msg.imageUrl || null)}
                                                />
                                            )}
                                            {msg.text && (
                                                <div className="p-2">
                                                    <p className="text-sm">{msg.text}</p>
                                                    {msg.edited && <span className="text-[10px] opacity-70 block text-right italic mt-1">edited</span>}
                                                </div>
                                            )}
                                        </div>
                                        {/* Visible "More Options" button for easier deletion/interaction */}
                                        <button 
                                            onClick={(e) => openMenuForMessage(e, msg)}
                                            className={`absolute top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity focus:opacity-100 ${isMe ? '-left-8' : '-right-8'}`}
                                            aria-label="Message options"
                                        >
                                            <i className="ph-bold ph-dots-three-vertical"></i>
                                        </button>
                                    </div>
                                </div>
                                {hasReactions && (
                                     <div className={`flex items-center gap-1 mt-1 px-2 ${isMe ? 'mr-8' : 'ml-8'}`}>
                                        {Object.entries(msg.reactions!).map(([emoji, uids]) => (
                                            Array.isArray(uids) && uids.length > 0 && (
                                                <div key={emoji} className="bg-gray-200 rounded-full px-2 py-0.5 flex items-center text-xs">
                                                    <span>{emoji}</span>
                                                    <span className="ml-1 font-semibold text-gray-600">{uids.length}</span>
                                                </div>
                                            )
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
                <div ref={messagesEndRef} />
            </main>

            {/* Input Form */}
            <footer className="flex-shrink-0 p-3 bg-white border-t pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                {imageToSend && (
                    <div className="relative w-24 h-24 mb-2 p-1 border rounded-lg">
                        <img src={imageToSend.previewUrl} alt="Preview" className="w-full h-full object-cover rounded"/>
                        <button 
                            onClick={() => {
                                setImageToSend(null);
                                if(fileInputRef.current) fileInputRef.current.value = '';
                            }} 
                            className="absolute -top-2 -right-2 bg-gray-700 text-white h-6 w-6 rounded-full text-xs"
                            type="button"
                        >
                            &times;
                        </button>
                        {isUploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><div className="spinner-white"></div></div>}
                    </div>
                )}
                <form onSubmit={handleSendMessage} className="flex items-center space-x-3">
                     <div className="relative">
                        <button type="button" onClick={() => setAttachmentMenuOpen(!isAttachmentMenuOpen)} className="text-gray-500 hover:text-purple-600">
                            <i className="ph-duotone ph-paperclip text-2xl"></i>
                        </button>
                        {isAttachmentMenuOpen && (
                            <div className="absolute bottom-full mb-2 w-48 bg-white rounded-lg shadow-xl border z-50">
                                <button onClick={() => {setCameraOpen(true); setAttachmentMenuOpen(false);}} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"><i className="ph-duotone ph-camera"></i> Take a Photo</button>
                                <button onClick={() => fileInputRef.current?.click()} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"><i className="ph-duotone ph-image"></i> Upload Image</button>
                                <button onClick={() => {setShowGiphy(true); setAttachmentMenuOpen(false);}} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"><i className="ph-duotone ph-gif"></i> GIPHY</button>
                                <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
                            </div>
                        )}
                    </div>
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 border-gray-300 rounded-full px-4 py-2 focus:ring-purple-500 focus:border-purple-500"
                        autoComplete="off"
                    />
                    <button type="submit" disabled={(!newMessage.trim() && !imageToSend) || isUploading} className="bg-purple-600 text-white rounded-full h-10 w-10 flex items-center justify-center disabled:bg-gray-400">
                        {isUploading ? <div className="spinner-white h-5 w-5"></div> : <i className="ph-duotone ph-paper-plane-tilt text-xl"></i>}
                    </button>
                </form>
            </footer>

            <ImageViewerModal isOpen={!!viewingImage} onClose={() => setViewingImage(null)} imageUrl={viewingImage || ''} />
            <CameraModal isOpen={isCameraOpen} onClose={() => setCameraOpen(false)} onImageSelected={handleImageFromCamera}/>

            {showGiphy && (
                <GiphyPicker 
                    onSelect={(url) => handleSendMessage(undefined, url)}
                    onClose={() => setShowGiphy(false)}
                />
            )}

            {/* Interaction Menu */}
            <InteractionMenu 
                isOpen={!!activeMessage && !!menuPosition}
                onClose={() => { setActiveMessage(null); setMenuPosition(null); }}
                position={menuPosition || { top: 0, left: 0, width: 0, height: 0 }}
                onReaction={handleReaction}
                onAction={handleAction}
                isOwner={activeMessage?.senderId === userProfile?.uid}
                type="message"
            />

            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl p-4 w-full max-w-sm">
                        <h3 className="font-bold mb-2">Edit Message</h3>
                        <textarea 
                            value={editModalText} 
                            onChange={e => setEditModalText(e.target.value)}
                            className="w-full border rounded p-2 mb-4"
                            rows={3}
                        />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                            <button onClick={handleEditSubmit} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Modal */}
            {isReportModalOpen && (
                <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl p-4 w-full max-w-sm">
                         <h3 className="font-bold mb-2 text-red-600">Report Message</h3>
                         <p className="text-sm text-gray-600 mb-2">Why are you reporting this message?</p>
                         <select 
                            value={reportReason} 
                            onChange={e => setReportReason(e.target.value)}
                            className="w-full border rounded p-2 mb-4"
                         >
                             <option value="">Select a reason</option>
                             <option value="harassment">Harassment</option>
                             <option value="spam">Spam</option>
                             <option value="inappropriate">Inappropriate Content</option>
                             <option value="other">Other</option>
                         </select>
                         <div className="flex justify-end gap-2">
                            <button onClick={() => setReportModalOpen(false)} className="px-4 py-2 text-gray-600">Cancel</button>
                            <button onClick={handleReportSubmit} disabled={!reportReason} className="px-4 py-2 bg-red-600 text-white rounded disabled:opacity-50">Report</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatConversationPage;